import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Card, DatePicker, Descriptions, Flex, Select, Space, Table, Tabs, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { CheckOutlined, CloseOutlined, PaperClipOutlined, PlusOutlined, RollbackOutlined } from '@ant-design/icons'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { Dayjs } from 'dayjs'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { useAuth } from '@/shared/auth/useAuth'
import { usePermissions } from '@/shared/auth/usePermissions'
import { defaultDayRange, rangeToIso, tenantToday } from '@/shared/lib/dayRange'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import {
  applyLocalList,
  byInstant,
  byNumber,
  byText,
  localSortChange,
  sortColumn,
  useLocalListState,
} from '@/shared/lib/localList'
import { formatMoneyValue, negateMoney, type Money } from '@/shared/lib/money'
import { moneyFlow, sumMoney, type MoneyFlow } from '@/shared/lib/moneyTotal'
import { PageHeader } from '@/shared/ui/PageHeader'
import { DataTable } from '@/shared/ui/table/DataTable'
import { RowActions } from '@/shared/ui/table/RowActions'
import { renderSummary, type SummaryLine } from '@/shared/ui/table/tableSummary'
import {
  DOCUMENT_TYPES,
  ENTRY_STATES,
  decideEntry,
  fetchBranchPeriod,
  fetchCashHistory,
  fetchEntryPage,
  type BranchPeriod,
  type CashBranch,
  type CashEntry,
  type CashHistoryRow,
  type EntryFilter,
  type EntryKind,
  type EntryState,
} from '@/features/cashdesk/cashdeskApi'
import { CashAttachmentsModal, type AttachmentTarget } from '@/features/cashdesk/CashAttachmentsModal'
import { CashBranchSelect } from '@/features/cashdesk/CashBranchSelect'
import { CashDecisionModal, type CashDecision } from '@/features/cashdesk/CashDecisionModal'
import { CashFormModal } from '@/features/cashdesk/CashFormModal'
import { transferDocumentText } from '@/features/cashdesk/transferText'
import { useTransferTypes } from '@/features/cashdesk/transferTypeApi'
import { useActiveBranches } from '@/features/branch/useActiveBranches'
import { useCashBranches } from '@/features/cashdesk/useCashBranches'

const ENTRY_STATE_COLOR: Record<EntryState, string> = {
  PENDING: 'gold',
  APPROVED: 'green',
  REJECTED: 'red',
  REVERSED: 'orange',
}

const ENTRY_OUTFLOW: Record<EntryKind, boolean> = {
  OTHER_INCOME: false,
  EXPENSE: true,
  ADJUSTMENT_SHORT: true,
  ADJUSTMENT_OVER: false,
}

const HISTORY_DAYS = 7
const HISTORY_MAX_DAYS = 93

function Strong({ children }: { children: string }) {
  return <Typography.Text strong>{children}</Typography.Text>
}

function SignedMoney({ value }: { value: Money | null }) {
  const type = value === null || value.amount === 0 ? undefined : value.amount < 0 ? 'danger' : 'success'
  return (
    <Typography.Text strong type={type}>
      {formatMoneyValue(value)}
    </Typography.Text>
  )
}

function flowLines(
  flow: MoneyFlow | null,
  labelColumn: string,
  amountColumn: string,
  labels: readonly [inflow: string, outflow: string, net: string],
): SummaryLine[] {
  if (flow === null) return []
  const line = (key: keyof MoneyFlow, label: string): SummaryLine => ({
    key,
    cells: { [labelColumn]: <Strong>{label}</Strong>, [amountColumn]: <SignedMoney value={flow[key]} /> },
  })
  return [line('inflow', labels[0]), line('outflow', labels[1]), line('net', labels[2])]
}

type BranchRow = CashBranch & BranchPeriod

export function BranchCashPage() {
  const { t } = useTranslation()
  const branches = useCashBranches()
  const [selected, setSelected] = useState<string | null>(null)
  const branchId = selected ?? branches.sole?.id ?? null
  const branch = branches.all.find((b) => b.id === branchId) ?? null
  const list = useLocalListState({ prefix: 'b' })
  const { selected: activeBranches } = useActiveBranches()
  const [picked, setPicked] = useState<string[]>(() => [...activeBranches])
  const activeKey = activeBranches.join()
  useEffect(() => {
    setPicked(activeKey ? activeKey.split(',') : [])
  }, [activeKey])
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null)
  const span = useMemo(() => (range ? rangeToIso(range) : null), [range])

  const period = useQuery({
    queryKey: queryKeys.cashdesk.period(picked, span?.from ?? null, span?.to ?? null),
    queryFn: ({ signal }) => fetchBranchPeriod(picked, span?.from ?? null, span?.to ?? null, signal),
    enabled: !branches.unknown,
  })

  const rows = useMemo<BranchRow[]>(() => {
    const byId = new Map((period.data ?? []).map((row) => [row.id, row]))
    return branches.all
      .filter((branch) => picked.length === 0 || picked.includes(branch.id))
      .flatMap((branch) => {
        const figures = byId.get(branch.id)
        return figures ? [{ ...branch, ...figures }] : []
      })
  }, [branches.all, period.data, picked])

  const visible = useMemo(
    () =>
      applyLocalList(rows, {
        search: '',
        filterState: {},
        comparators: {
          name: byText((row) => row.name),
          opening: byNumber((row) => row.opening.amount),
          received: byNumber((row) => row.received.amount),
          paidOut: byNumber((row) => row.paidOut.amount),
          closing: byNumber((row) => row.closing.amount),
          inTransit: byNumber((row) => row.inTransit.amount),
        },
        sort: list.sort,
      }),
    [rows, list.sort],
  )

  const money = useCallback(
    (key: 'opening' | 'received' | 'paidOut' | 'closing' | 'inTransit', title: string) => ({
      key,
      title,
      width: 160,
      align: 'right' as const,
      ...sortColumn(list.sort, key),
      render: (_: unknown, row: BranchRow) => formatMoneyValue(row[key]),
    }),
    [list.sort],
  )

  const columns = useMemo<ColumnsType<BranchRow>>(
    () => [
      {
        key: 'name',
        title: t('cashdesk.columns.branch'),
        ...sortColumn(list.sort, 'name'),
        render: (_, row) => (
          <Space size={4}>
            <span>{row.name}</span>
            <Typography.Text type="secondary">({row.code})</Typography.Text>
            {row.state === 'ACTIVE' ? null : <Tag>{t(`orgNodeState.${row.state}`, { defaultValue: row.state })}</Tag>}
          </Space>
        ),
      },
      money('opening', t('cashdesk.period.opening')),
      money('received', t('cashdesk.period.received')),
      money('paidOut', t('cashdesk.period.paidOut')),
      money('closing', t('cashdesk.period.closing')),
      money('inTransit', t('cashdesk.balance.inTransit')),
    ],
    [t, list.sort, money],
  )

  const totals: SummaryLine[] =
    visible.length > 0
      ? [
          {
            key: 'total',
            cells: {
              name: <Strong>{t('common.total')}</Strong>,
              opening: <Strong>{formatMoneyValue(sumMoney(visible.map((row) => row.opening)))}</Strong>,
              received: <Strong>{formatMoneyValue(sumMoney(visible.map((row) => row.received)))}</Strong>,
              paidOut: <Strong>{formatMoneyValue(sumMoney(visible.map((row) => row.paidOut)))}</Strong>,
              closing: <Strong>{formatMoneyValue(sumMoney(visible.map((row) => row.closing)))}</Strong>,
              inTransit: <Strong>{formatMoneyValue(sumMoney(visible.map((row) => row.inTransit)))}</Strong>,
            },
          },
        ]
      : []

  return (
    <>
      <PageHeader title={t('nav.branchCash')} description={t('pageIntro.branchCash')} />

      <Card
        title={t('cashdesk.branches.title')}
        style={{ marginBottom: 16 }}
        extra={
          <Space wrap>
            <Select<string[]>
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              style={{ minWidth: 240 }}
              placeholder={t('cashdesk.period.allBranches')}
              value={picked}
              onChange={setPicked}
              optionFilterProp="label"
              options={branches.all.map((b) => ({ value: b.id, label: `${b.name} (${b.code})` }))}
            />
            <DatePicker.RangePicker
              allowClear
              value={range}
              placeholder={[t('cashdesk.period.allTime'), t('cashdesk.period.allTime')]}
              disabledDate={(day) => day.isAfter(tenantToday(), 'day')}
              onChange={(value) => setRange(value && value[0] && value[1] ? [value[0], value[1]] : null)}
            />
          </Space>
        }
      >
        {branches.error ? <Alert type="error" showIcon message={translateError(branches.error, t)} /> : null}
        {period.error ? <Alert type="error" showIcon message={translateError(period.error, t)} /> : null}
        <Table<BranchRow>
          size="small"
          rowKey={(row) => row.id}
          columns={columns}
          dataSource={[...visible]}
          loading={branches.isPending || period.isPending}
          pagination={false}
          scroll={{ x: 1000 }}
          onChange={localSortChange<BranchRow>(list.patch)}
          summary={() => renderSummary(columns, totals, 1)}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: branchId ? [branchId] : [],
            onChange: (keys) => setSelected(keys[0] ? String(keys[0]) : null),
          }}
          onRow={(row) => ({ style: { cursor: 'pointer' }, onClick: () => setSelected(row.id) })}
          locale={{ emptyText: branches.error ? t('common.error') : t('cashdesk.branches.empty') }}
        />
      </Card>

      <Tabs
        items={[
          {
            key: 'history',
            label: t('cashdesk.history.title'),
            children: branch ? (
              <BranchHistory branch={branch} />
            ) : (
              <Typography.Text type="secondary">{t('cashdesk.history.pickBranch')}</Typography.Text>
            ),
          },
          {
            key: 'entries',
            label: t('cashdesk.entry.title'),
            children: <EntryList branches={branches.all} branchId={branchId} />,
          },
        ]}
      />
    </>
  )
}

function BranchHistory({ branch }: { branch: CashBranch }) {
  const { t } = useTranslation()
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => defaultDayRange(HISTORY_DAYS))
  const span = useMemo(() => rangeToIso(range), [range])
  const tooLong = range[1].diff(range[0], 'day') > HISTORY_MAX_DAYS
  const list = useLocalListState({ prefix: 'h' })
  const transferTypes = useTransferTypes()

  const history = useQuery({
    queryKey: queryKeys.cashdesk.history(branch.id, span.from, span.to),
    queryFn: ({ signal }) => fetchCashHistory(branch.id, span.from, span.to, signal),
    enabled: !tooLong,
  })

  const rows = useMemo(() => history.data ?? [], [history.data])
  const kindLabel = useCallback((kind: string) => t(`txnKind.${kind}`, { defaultValue: kind }), [t])
  const kindOptions = useMemo(
    () =>
      [...new Set(rows.map((row) => row.txnKind))]
        .map((kind) => ({ value: kind, label: kindLabel(kind) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [rows, kindLabel],
  )

  const visible = useMemo(
    () =>
      applyLocalList(rows, {
        search: '',
        filters: {
          kind: (row, value) => row.txnKind === value,
          documentType: (row, value) => row.document?.type === value,
        },
        filterState: list.state.filters,
        comparators: {
          at: byInstant((row) => row.at),
          kind: byText((row) => kindLabel(row.txnKind)),
          document: byText((row) => row.document?.number ?? null),
          amount: byNumber((row) => row.amount.amount),
          txn: byNumber((row) => row.txnId),
        },
        sort: list.sort,
      }),
    [rows, list.state.filters, list.sort, kindLabel],
  )

  const columns: ColumnsType<CashHistoryRow> = [
    {
      key: 'at',
      title: t('cashdesk.columns.date'),
      width: 170,
      ...sortColumn(list.sort, 'at'),
      render: (_, row) => <span title={utcTooltip(row.at)}>{formatDateTime(row.at)}</span>,
    },
    {
      key: 'kind',
      title: t('cashdesk.history.kind'),
      width: 200,
      ...sortColumn(list.sort, 'kind'),
      render: (_, row) => kindLabel(row.txnKind),
    },
    {
      key: 'document',
      title: t('cashdesk.history.document'),
      width: 300,
      ...sortColumn(list.sort, 'document'),
      render: (_, row) =>
        row.document ? (
          <Space size={4}>
            <Tag>{t(`cashdesk.documentType.${row.document.type}`)}</Tag>
            <span>{transferDocumentText(row.document, transferTypes.data, t)}</span>
          </Space>
        ) : (
          t('common.dash')
        ),
    },
    {
      key: 'amount',
      title: t('cashdesk.columns.amount'),
      width: 160,
      align: 'right',
      ...sortColumn(list.sort, 'amount'),
      render: (_, row) => (
        <Typography.Text type={row.amount.amount < 0 ? 'danger' : 'success'}>
          {formatMoneyValue(row.amount)}
        </Typography.Text>
      ),
    },
    {
      key: 'txn',
      title: t('cashdesk.receipt.txn'),
      width: 110,
      ...sortColumn(list.sort, 'txn'),
      render: (_, row) => `#${row.txnId}`,
    },
  ]

  const totals = flowLines(moneyFlow(visible.map((row) => row.amount)), 'at', 'amount', [
    t('cashdesk.totals.inflow'),
    t('cashdesk.totals.outflow'),
    t('cashdesk.totals.net'),
  ])

  return (
    <Card>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Flex wrap gap="small" align="center">
          <Typography.Text strong>
            {branch.name} ({branch.code})
          </Typography.Text>
          <DatePicker.RangePicker
            value={range}
            allowClear={false}
            onChange={(value) => {
              if (value && value[0] && value[1]) setRange([value[0], value[1]])
            }}
            disabledDate={(d) => d.isAfter(tenantToday(), 'day')}
          />
          <Select<string>
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ minWidth: 200 }}
            placeholder={t('cashdesk.history.kind')}
            value={list.filter('kind') ?? undefined}
            onChange={(value) => list.setFilter('kind', value ?? null)}
            options={kindOptions}
          />
          <Select<string>
            allowClear
            style={{ minWidth: 170 }}
            placeholder={t('cashdesk.history.documentType')}
            value={list.filter('documentType') ?? undefined}
            onChange={(value) => list.setFilter('documentType', value ?? null)}
            options={DOCUMENT_TYPES.map((type) => ({ value: type, label: t(`cashdesk.documentType.${type}`) }))}
          />
          {list.narrowed ? (
            <>
              <Typography.Text type="secondary">
                {t('listView.shown', { shown: visible.length, total: rows.length })}
              </Typography.Text>
              <Button type="link" size="small" onClick={list.reset}>
                {t('dataTable.clearFilters')}
              </Button>
            </>
          ) : null}
        </Flex>
        {tooLong ? (
          <Alert type="warning" showIcon message={t('cashdesk.history.tooLong', { days: HISTORY_MAX_DAYS })} />
        ) : null}
        {history.isError ? <Alert type="error" showIcon message={translateError(history.error, t)} /> : null}
        <Table<CashHistoryRow>
          size="small"
          rowKey={(row) => String(row.entryId)}
          columns={columns}
          dataSource={[...visible]}
          loading={history.isPending && !tooLong}
          pagination={{ pageSize: 50, hideOnSinglePage: true }}
          scroll={{ x: 940 }}
          onChange={localSortChange<CashHistoryRow>(list.patch)}
          summary={() => renderSummary(columns, totals)}
          locale={{ emptyText: history.isError ? t('common.error') : t('cashdesk.history.empty') }}
        />
        {totals.length > 0 ? (
          <Typography.Text type="secondary">{t('cashdesk.totals.periodNote')}</Typography.Text>
        ) : null}
      </Space>
    </Card>
  )
}

function EntryList({ branches, branchId }: { branches: readonly CashBranch[]; branchId: string | null }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { can } = usePermissions()
  const queryClient = useQueryClient()
  const [override, setOverride] = useState<{ selected: string | null; branch: string | null } | null>(null)
  const filterBranch = override !== null && override.selected === branchId ? override.branch : branchId
  const [state, setState] = useState<EntryState | null>(null)
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null)
  const [creating, setCreating] = useState(false)
  const [decision, setDecision] = useState<CashDecision | null>(null)
  const [attachments, setAttachments] = useState<AttachmentTarget | null>(null)

  const filter = useMemo<EntryFilter>(() => {
    const span = range ? rangeToIso(range) : null
    return { branchId: filterBranch, state, from: span?.from ?? null, to: span?.to ?? null }
  }, [filterBranch, state, range])
  const fetchPage = useMemo(() => fetchEntryPage(filter), [filter])

  const columns = useMemo<ColumnsType<CashEntry>>(() => {
    const details = (row: CashEntry) => (
      <Descriptions size="small" column={1} bordered>
        <Descriptions.Item label={t('cashdesk.entry.kind')}>{t(`cashdesk.entryKind.${row.kind}`)}</Descriptions.Item>
        <Descriptions.Item label={t('cashdesk.columns.branch')}>{row.branch.name}</Descriptions.Item>
        <Descriptions.Item label={t('cashdesk.columns.amount')}>{formatMoneyValue(row.amount)}</Descriptions.Item>
        <Descriptions.Item label={t('actions.reason')}>{row.reason}</Descriptions.Item>
      </Descriptions>
    )
    const own = (row: CashEntry) => row.requestedBy.id === user?.id
    const ask = (row: CashEntry, kind: 'approve' | 'reject' | 'reverse') =>
      setDecision({
        title: t(`cashdesk.entry.${kind}Title`),
        okText: t(`cashdesk.entry.${kind}`),
        danger: kind !== 'approve',
        warning: t(`cashdesk.entry.${kind}Warn`),
        reasonRequired: kind !== 'approve',
        details: details(row),
        submit: (text) => decideEntry(row.id, kind, text),
      })

    return [
      {
        key: 'requestedAt',
        title: t('cashdesk.columns.date'),
        width: 170,
        sorter: true,
        render: (_, row) => <span title={utcTooltip(row.requestedAt)}>{formatDateTime(row.requestedAt)}</span>,
      },
      {
        key: 'kind',
        title: t('cashdesk.entry.kind'),
        width: 180,
        sorter: true,
        render: (_, row) => t(`cashdesk.entryKind.${row.kind}`),
      },
      {
        key: 'state',
        title: t('cashdesk.columns.state'),
        width: 130,
        sorter: true,
        render: (_, row) => <Tag color={ENTRY_STATE_COLOR[row.state]}>{t(`cashdesk.entryState.${row.state}`)}</Tag>,
      },
      {
        key: 'amount',
        title: t('cashdesk.columns.amount'),
        width: 140,
        align: 'right',
        sorter: true,
        render: (_, row) => formatMoneyValue(row.amount),
      },
      { key: 'branch', title: t('cashdesk.columns.branch'), width: 160, render: (_, row) => row.branch.name },
      {
        key: 'reason',
        title: t('actions.reason'),
        width: 260,
        render: (_, row) => (row.category ? `${row.category} — ${row.reason}` : row.reason),
      },
      {
        key: 'requestedBy',
        title: t('cashdesk.entry.requestedBy'),
        width: 160,
        render: (_, row) => row.requestedBy.name,
      },
      {
        key: 'decidedBy',
        title: t('cashdesk.entry.decidedBy'),
        width: 200,
        render: (_, row) => (row.decidedBy ? `${row.decidedBy.name} · ${formatDateTime(row.decidedAt)}` : t('common.dash')),
      },
      {
        key: 'actions',
        title: '',
        width: 56,
        align: 'center',
        fixed: 'right',
        render: (_, row) => (
          <RowActions
            items={[
              {
                key: 'attachments',
                label: t('cashdesk.attachment.open', { count: row.attachmentCount }),
                icon: <PaperClipOutlined />,
                onClick: () =>
                  setAttachments({
                    owner: 'entries',
                    id: row.id,
                    title: `${t(`cashdesk.entryKind.${row.kind}`)} · ${row.branch.name}`,
                    canUpload: row.state === 'PENDING' && can('cash.entry.create'),
                  }),
              },
              {
                key: 'approve',
                label: t('cashdesk.entry.approve'),
                icon: <CheckOutlined />,
                permission: 'cash.entry.approve',
                divider: true,
                hidden: row.state !== 'PENDING' || own(row),
                onClick: () => ask(row, 'approve'),
              },
              {
                key: 'reject',
                label: t('cashdesk.entry.reject'),
                icon: <CloseOutlined />,
                permission: 'cash.entry.approve',
                danger: true,
                hidden: row.state !== 'PENDING' || own(row),
                onClick: () => ask(row, 'reject'),
              },
              {
                key: 'selfHint',
                label: t('cashdesk.selfDecisionHint'),
                permission: 'cash.entry.approve',
                divider: true,
                disabled: true,
                hidden: row.state !== 'PENDING' || !own(row),
                onClick: () => undefined,
              },
              {
                key: 'reverse',
                label: t('cashdesk.entry.reverse'),
                icon: <RollbackOutlined />,
                permission: 'cash.entry.approve',
                danger: true,
                hidden: row.state !== 'APPROVED',
                onClick: () => ask(row, 'reverse'),
              },
            ]}
          />
        ),
      },
    ]
  }, [t, user?.id, can])

  return (
    <>
      <Space wrap style={{ marginBottom: 16 }}>
        <CashBranchSelect
          branches={branches}
          value={filterBranch}
          onChange={(branch) => setOverride({ selected: branchId, branch })}
          allowClear
          placeholder={t('cashdesk.allBranches')}
        />
        <Select<string>
          style={{ minWidth: 170 }}
          value={state ?? 'ALL'}
          onChange={(value) => setState(value === 'ALL' ? null : (value as EntryState))}
          options={[
            { value: 'ALL', label: t('cashdesk.allStates') },
            ...ENTRY_STATES.map((s) => ({ value: s, label: t(`cashdesk.entryState.${s}`) })),
          ]}
        />
        <DatePicker.RangePicker
          allowClear
          value={range}
          onChange={(value) => setRange(value && value[0] && value[1] ? [value[0], value[1]] : null)}
          disabledDate={(d) => d.isAfter(tenantToday(), 'day')}
        />
        <Can permission="cash.entry.create">
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
            {t('cashdesk.entry.create')}
          </Button>
        </Can>
      </Space>

      <DataTable<CashEntry>
        queryKey={[...queryKeys.cashdesk.entries, filter]}
        fetchPage={fetchPage}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="cash-entries"
        urlPrefix="e"
        scrollX={1500}
        stickyFirstColumn
        showTotal={false}
        emptyTitle={t('cashdesk.entry.empty')}
        summary={(rows) =>
          flowLines(
            moneyFlow(rows.map((row) => (ENTRY_OUTFLOW[row.kind] ? negateMoney(row.amount) : row.amount))),
            'requestedAt',
            'amount',
            [t('cashdesk.totals.pageInflow'), t('cashdesk.totals.pageOutflow'), t('cashdesk.totals.pageNet')],
          )
        }
      />

      <CashFormModal mode={creating ? 'entry' : null} onClose={() => setCreating(false)} />
      <CashDecisionModal
        decision={decision}
        onClose={() => setDecision(null)}
        onDone={() => {
          setDecision(null)
          void queryClient.invalidateQueries({ queryKey: queryKeys.cashdesk.all })
        }}
      />
      <CashAttachmentsModal target={attachments} onClose={() => setAttachments(null)} />
    </>
  )
}
